import { useEffect, useMemo, useState } from "react";
import { FileText, Download, Loader2, ChevronRight, ArrowLeft, FileType2, FileBadge } from "lucide-react";
import { toast } from "sonner";
import {
  listDocumentTemplates,
  generateDocumentTemplate,
  documentTemplateDownloadUrl,
} from "@/lib/api";

/**
 * Document Templates — incorporation forms, partnership deeds, audit reports etc.
 *
 * UX:
 *   Left:  List of templates grouped by category
 *   Right: Form for the selected template, with "Generate DOCX + PDF" button
 *
 * Generated files are downloaded directly via <a download> links (no client-side
 * binary handling — the backend job_id route serves them).
 */

const CATEGORY_LABELS = {
  incorporation: "Incorporation Forms",
  partnership: "Partnership Documents",
  audit: "Audit Reports",
  schedule3: "Schedule III Financials",
};

export default function DocumentTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState(null);
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [lastJob, setLastJob] = useState(null);

  useEffect(() => {
    setLoading(true);
    listDocumentTemplates()
      .then((r) => {
        const list = r.data?.templates || [];
        setTemplates(list);
        if (list.length && !activeKey) {
          setActiveKey(list[0].key);
        }
      })
      .catch(() => toast.error("Failed to load templates"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const active = useMemo(
    () => templates.find((t) => t.key === activeKey),
    [templates, activeKey],
  );

  // Reset values + last job whenever the active template changes
  useEffect(() => {
    if (!active) return;
    const initial = {};
    for (const f of active.fields) initial[f.key] = f.default || "";
    setValues(initial);
    setLastJob(null);
  }, [activeKey, active]);

  const grouped = useMemo(() => {
    const out = {};
    for (const t of templates) {
      (out[t.category] ||= []).push(t);
    }
    return out;
  }, [templates]);

  const handleGenerate = async () => {
    if (!active) return;
    // Validate required
    const missing = active.fields.filter((f) => f.required && !String(values[f.key] || "").trim());
    if (missing.length) {
      toast.error(`Please fill: ${missing.map((m) => m.label).join(", ")}`);
      return;
    }
    setBusy(true);
    setLastJob(null);
    try {
      const r = await generateDocumentTemplate(active.key, values, true);
      setLastJob(r.data);
      toast.success("Document ready — download buttons enabled below");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="document-templates-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-zinc-500">
            <FileText size={14} className="text-[#002FA7]" /> Document Templates
          </div>
          <h1 className="font-semibold text-2xl sm:text-3xl tracking-tight mt-2 text-zinc-950">
            Templates &amp; Document Generator
          </h1>
          <p className="mt-1 text-sm text-zinc-600 max-w-2xl">
            Generate incorporation forms, partnership deeds, audit reports and Schedule III
            financials from your firm's master templates. Fill the fields once — download in
            DOCX (editable) and PDF (locked) formats.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-zinc-500">
          <Loader2 className="animate-spin mr-2" size={18} /> Loading templates…
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
          {/* Left — template list grouped by category */}
          <aside className="border border-zinc-200 bg-white" data-testid="templates-list">
            {Object.entries(grouped).map(([cat, items]) => (
              <div key={cat} className="border-b border-zinc-100 last:border-0">
                <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-medium">
                  {CATEGORY_LABELS[cat] || cat}
                </div>
                {items.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setActiveKey(t.key)}
                    data-testid={`template-tab-${t.key}`}
                    className={`w-full text-left px-4 py-3 text-sm border-l-2 flex items-center justify-between gap-2 transition-colors ${
                      t.key === activeKey
                        ? "border-[#002FA7] text-[#002FA7] bg-blue-50/40 font-medium"
                        : "border-transparent text-zinc-700 hover:bg-zinc-50"
                    }`}
                  >
                    <span className="truncate">{t.title}</span>
                    <ChevronRight size={14} className="shrink-0 opacity-50" />
                  </button>
                ))}
              </div>
            ))}
          </aside>

          {/* Right — fill form */}
          <main className="border border-zinc-200 bg-white p-6 sm:p-8" data-testid="template-form">
            {active ? (
              <>
                <div className="flex items-start justify-between gap-4 mb-1">
                  <div>
                    <h2 className="text-xl font-semibold text-zinc-950">{active.title}</h2>
                    <p className="mt-1 text-sm text-zinc-600 max-w-2xl">{active.description}</p>
                  </div>
                  <button
                    className="lg:hidden p-2 border border-zinc-200 hover:border-[#002FA7]"
                    onClick={() => setActiveKey(null)}
                    aria-label="Back"
                  >
                    <ArrowLeft size={14} />
                  </button>
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {active.fields.map((f) => (
                    <div
                      key={f.key}
                      className={f.kind === "textarea" ? "md:col-span-2" : ""}
                    >
                      <label
                        htmlFor={`field-${f.key}`}
                        className="block text-[11px] uppercase tracking-[0.14em] text-zinc-600 font-medium mb-1.5"
                      >
                        {f.label}{f.required && <span className="text-red-500"> *</span>}
                      </label>
                      {f.kind === "textarea" ? (
                        <textarea
                          id={`field-${f.key}`}
                          data-testid={`field-${f.key}`}
                          rows={3}
                          value={values[f.key] || ""}
                          onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                          className="w-full border border-zinc-200 px-3 py-2 text-sm focus:border-[#002FA7] focus:outline-none"
                        />
                      ) : (
                        <input
                          id={`field-${f.key}`}
                          data-testid={`field-${f.key}`}
                          type={f.kind === "number" ? "number" : "text"}
                          value={values[f.key] || ""}
                          onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                          className="w-full border border-zinc-200 px-3 py-2 text-sm focus:border-[#002FA7] focus:outline-none"
                        />
                      )}
                      {f.hint && (
                        <p className="mt-1 text-[11px] text-zinc-500">{f.hint}</p>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-zinc-100 pt-6">
                  <button
                    onClick={handleGenerate}
                    disabled={busy}
                    data-testid="generate-document-button"
                    className="px-4 py-2.5 bg-[#002FA7] text-white text-sm font-medium hover:bg-[#001E70] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                    {busy ? "Generating…" : "Generate Document"}
                  </button>

                  {lastJob && (
                    <>
                      <a
                        href={documentTemplateDownloadUrl(active.key, lastJob.job_id, "docx")}
                        download
                        data-testid="download-docx-button"
                        className="px-4 py-2.5 border border-[#002FA7] text-[#002FA7] text-sm font-medium hover:bg-blue-50 flex items-center gap-2"
                      >
                        <FileType2 size={14} /> Download .docx
                      </a>
                      {lastJob.pdf_available && (
                        <a
                          href={documentTemplateDownloadUrl(active.key, lastJob.job_id, "pdf")}
                          download
                          data-testid="download-pdf-button"
                          className="px-4 py-2.5 border border-[#002FA7] text-[#002FA7] text-sm font-medium hover:bg-blue-50 flex items-center gap-2"
                        >
                          <FileBadge size={14} /> Download .pdf
                        </a>
                      )}
                      <span className="text-[11px] text-zinc-500 ml-2 flex items-center gap-1">
                        <Download size={11} /> Ready · Job {lastJob.job_id.slice(0, 6)}
                      </span>
                    </>
                  )}
                </div>
              </>
            ) : (
              <div className="text-sm text-zinc-500">Select a template on the left.</div>
            )}
          </main>
        </div>
      )}
    </div>
  );
}
